(() => {
  const ROOT_SELECTOR = '[data-hpg-band-quality-video]';
  const PLAY_SELECTOR = '[data-hpg-band-quality-play]';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const MOBILE_QUERY = '(max-width: 749px)';
  const REVEAL_THRESHOLD = 0.25;

  class HpgBandQualityVideo {
    constructor(root) {
      this.root = root;
      this.playButton = root.querySelector(PLAY_SELECTOR);
      this.videos = Array.from(root.querySelectorAll('video'));
      this.isRevealLocked = false;
      this.revealObserver = null;
      this.resumeTimer = null;
      this.playAttempt = null;
      this.hasAutoPlayed = false;
      this.hasUserInteracted = false;
      this.shouldAutoPlay = root.dataset.hpgBandQualityAutoplay === 'true';
      this.hasClickControls = root.dataset.hpgBandQualityClickControls === 'true';
      this.reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onPlay = this.onPlay.bind(this);
      this.onPause = this.onPause.bind(this);
      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.onPlaybackActivity = this.onPlaybackActivity.bind(this);
    }

    connect() {
      if (this.root.dataset.hpgBandQualityVideoReady === 'true') return;

      this.root.dataset.hpgBandQualityVideoReady = 'true';
      if (this.hasClickControls || !this.shouldAutoPlay) {
        this.root.addEventListener('click', this.onClick);
      }
      if (this.hasClickControls) {
        this.root.addEventListener('keydown', this.onKeyDown);
      }
      this.videos.forEach((video) => {
        video.addEventListener('play', this.onPlay);
        video.addEventListener('pause', this.onPause);
        video.addEventListener('ended', this.onPause);
      });

      if (typeof this.reducedMotion.addEventListener === 'function') {
        this.reducedMotion.addEventListener('change', this.onReducedMotionChange);
      }
      if (typeof this.mobileQuery.addEventListener === 'function') {
        this.mobileQuery.addEventListener('change', this.onBreakpointChange);
      }

      this.setupReveal();
      this.setupAutoplay();
      this.syncState();
    }

    disconnect() {
      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('keydown', this.onKeyDown);
      this.videos.forEach((video) => {
        video.removeEventListener('play', this.onPlay);
        video.removeEventListener('pause', this.onPause);
        video.removeEventListener('ended', this.onPause);
      });

      if (typeof this.reducedMotion.removeEventListener === 'function') {
        this.reducedMotion.removeEventListener('change', this.onReducedMotionChange);
      }
      if (typeof this.mobileQuery.removeEventListener === 'function') {
        this.mobileQuery.removeEventListener('change', this.onBreakpointChange);
      }

      window.removeEventListener('scroll', this.onPlaybackActivity);
      document.removeEventListener('touchmove', this.onPlaybackActivity);
      document.removeEventListener('visibilitychange', this.onPlaybackActivity);
      this.disconnectRevealObserver();
      window.clearTimeout(this.resumeTimer);

      delete this.root.dataset.hpgBandQualityVideoReady;
    }

    getActiveVideo() {
      return this.videos.find((video) => this.isElementVisible(video)) || this.videos[0];
    }

    isElementVisible(element) {
      if (!element) return false;

      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;

      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }

    pauseInactiveVideos(activeVideo = null) {
      this.videos.forEach((video) => {
        if (video === activeVideo) return;
        video.pause();
      });
    }

    playVideo(video) {
      if (!video) return Promise.resolve(false);

      this.pauseInactiveVideos(video);
      if (this.playAttempt) return this.playAttempt;

      this.playAttempt = video.play()
        .then(() => true)
        .catch(() => {
          this.syncState();
          return false;
        })
        .finally(() => {
          this.playAttempt = null;
        });

      return this.playAttempt;
    }

    onClick(event) {
      if (event.target.closest('a')) return;

      this.togglePlayback();
    }

    onKeyDown(event) {
      if (event.repeat || (event.key !== 'Enter' && event.key !== ' ')) return;

      event.preventDefault();
      this.togglePlayback();
    }

    togglePlayback() {
      const video = this.getActiveVideo();
      if (!video) return;

      this.hasUserInteracted = true;

      if (video.paused) {
        this.playVideo(video);
      } else {
        video.pause();
      }
    }

    onPlay(event) {
      const activeVideo = this.getActiveVideo();
      const playedVideo = event?.currentTarget;

      if (playedVideo && playedVideo !== activeVideo) {
        playedVideo.pause();
        return;
      }

      this.pauseInactiveVideos(activeVideo);
      window.clearTimeout(this.resumeTimer);
      if (this.shouldAutoPlay && !this.hasUserInteracted) {
        this.hasAutoPlayed = true;
      }
      this.syncState();
    }

    onPause(event) {
      this.syncState();

      const pausedVideo = event?.currentTarget || this.getActiveVideo();
      if (!this.shouldResumeAutoplay(pausedVideo)) return;

      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => {
        if (this.shouldResumeAutoplay(pausedVideo)) this.playVideo(pausedVideo);
      }, 80);
    }

    onReducedMotionChange() {
      this.setupReveal();
    }

    onBreakpointChange() {
      this.configureVideoLoading();

      const video = this.getActiveVideo();
      if (this.shouldAutoPlay && this.isRevealLocked && !this.hasUserInteracted) {
        this.playVideo(video);
      }
    }

    onPlaybackActivity() {
      if (document.visibilityState === 'hidden') return;

      const video = this.getActiveVideo();
      if (this.shouldResumeAutoplay(video)) this.playVideo(video);
    }

    isInViewport() {
      const rect = this.root.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      return rect.top < viewportHeight && rect.bottom > 0;
    }

    shouldResumeAutoplay(video) {
      return Boolean(
        this.shouldAutoPlay
        && this.isRevealLocked
        && !this.hasUserInteracted
        && video
        && video === this.getActiveVideo()
        && video.paused
        && !video.ended
        && this.isInViewport()
      );
    }

    setupReveal() {
      this.root.classList.remove('is-reveal-ready', 'is-revealed');
      this.root.style.removeProperty('--hpg-band-quality-video-reveal');
      this.isRevealLocked = false;
      this.disconnectRevealObserver();

      this.root.classList.add('is-reveal-ready');

      if (this.reducedMotion.matches) {
        this.root.classList.add('is-revealed');
        this.root.style.setProperty('--hpg-band-quality-video-reveal', '1');
      }

      if (!('IntersectionObserver' in window)) {
        this.lockReveal();
        this.playWhenReady();
        return;
      }

      this.revealObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= REVEAL_THRESHOLD)) return;

        this.lockReveal();
        this.playWhenReady();
      }, {
        threshold: [REVEAL_THRESHOLD],
      });
      this.revealObserver.observe(this.root.parentElement || this.root);
    }

    setupAutoplay() {
      if (!this.shouldAutoPlay || this.videos.length === 0) return;

      this.configureVideoLoading();

      this.videos.forEach((video) => {
        video.autoplay = false;
        video.removeAttribute('autoplay');
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');

        if (!video.paused) {
          video.pause();
        }
      });

      if (this.mobileQuery.matches) {
        window.addEventListener('scroll', this.onPlaybackActivity, { passive: true });
        document.addEventListener('touchmove', this.onPlaybackActivity, { passive: true });
      }
      document.addEventListener('visibilitychange', this.onPlaybackActivity);

    }

    configureVideoLoading() {
      const activeVideo = this.getActiveVideo();

      this.videos.forEach((video) => {
        const isActive = video === activeVideo;
        video.preload = isActive ? 'auto' : 'none';

        if (isActive && video.readyState === 0) {
          video.load();
        }

        if (!isActive && !video.paused) {
          video.pause();
        }
      });
    }

    disconnectRevealObserver() {
      if (!this.revealObserver) return;

      this.revealObserver.disconnect();
      this.revealObserver = null;
    }

    async playWhenReady() {
      if (!this.shouldAutoPlay || this.hasAutoPlayed || this.hasUserInteracted) return;

      const video = this.getActiveVideo();
      if (!video) return;

      video.autoplay = true;
      video.setAttribute('autoplay', '');
      const didStart = await this.playVideo(video);
      if (!didStart) return;

      this.hasAutoPlayed = true;
    }

    lockReveal() {
      this.isRevealLocked = true;
      this.root.classList.add('is-revealed');
      this.root.style.setProperty('--hpg-band-quality-video-reveal', '1');
      this.disconnectRevealObserver();
    }

    syncState() {
      const video = this.getActiveVideo();
      const isPlaying = Boolean(video && !video.paused && !video.ended);

      this.root.classList.toggle('is-playing', isPlaying);

      if (this.playButton) {
        this.playButton.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
      }
      if (this.hasClickControls) {
        this.root.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
        this.root.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
      }
    }
  }

  const instances = new WeakMap();

  const getRoots = (container) => {
    const roots = [];

    if (container.matches?.(ROOT_SELECTOR)) {
      roots.push(container);
    }

    container.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    return roots;
  };

  const setup = (container = document) => {
    getRoots(container).forEach((root) => {
      if (instances.has(root)) return;

      const instance = new HpgBandQualityVideo(root);
      instances.set(root, instance);
      instance.connect();
    });
  };

  const teardown = (container = document) => {
    getRoots(container).forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;

      instance.disconnect();
      instances.delete(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => setup());
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));
})();
